import React, { useMemo, useCallback } from 'react';
import { Marker, InfoBox } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { Button } from 'src/components';
import { IconClose } from 'src/icons';
import { useCreateModal, useEditData } from 'src/stores';
import { markerCDamage, markerPDamage, markerFDamage, markerLDamage } from 'src/images';
import { damage_category } from 'src/utils/choises';
import { Damage } from 'src/pages/Home/Home';
import styles from './DamageMarker.module.scss';
import axios from 'axios';//20240412 Add Uduki

type Props = {
  damage: Damage;
  activeDamageId: number | null;
  d_activeMarker: (v: Damage) => void;
  d_closeInfoBox: () => void;
};

// InfoBox オプション値
const infoBoxOptions = {
  disableAutoPan: true,
  alignBottom: true,
  maxWidth: 270,
  boxClass: styles.infobox,
};

// 備考制限設定
const l_limit = `${process.env.REACT_APP_HOME_REMARKS_LIMIT_LINE}`+"em";

/**
 * DamageMarker コンポーネント
 * @returns React.ReactNode
 */
const DamageMarker: React.FC<Props> = ({ damage, activeDamageId, d_activeMarker, d_closeInfoBox }) => {
  /**
   * 害獣座標をメモ化
   */
  const latlng = useMemo(() => new google.maps.LatLng({
    lat: Number(damage.damage_latitude),
    lng: Number(damage.damage_longitude),
  }), [damage]);

  const [cookie] = useCookies();

  /**
 * vermin_kind_name からマーカー画像をセレクト
 * @example
 * <Marker icon={marketFormat([vermin_kind_id])} ... />
 */
  const markerFormat = useCallback((kindName: number) => {
    let marker;
    switch (kindName) {
      case 1:
        marker = markerFDamage;
        break;
      case 2:
        marker = markerCDamage;
        break;
      case 4:
        marker = markerPDamage;
        break;
      case 5:
        marker = markerLDamage;
        break;
      default:
        marker = '';
    }
    return marker;
  }, []);

  /**
   * InfoBox を表示するハンドラ
   * @example
   * <Marker onClick={handleMarkerClick} ... />
   */
  const handleMarkerClick = useCallback(() => {
    //console.log(l_limit);
    d_activeMarker(damage);
  }, [d_activeMarker, damage]);

  /**
 * 報告書修正モーダル を表示するハンドラ
 * @example
 * <Button onClick={handleEditModal} ... />
 * 20240412 Add Uduki
 */
  const handleEditReportModal = useCallback(() => {
    axios.post(`${process.env.REACT_APP_DEV_API_URL}/report_data`, {
      report_kind:2,
      information_id:damage.damage_id,
    },{
      headers: { 
        Authorization: `Bearer ${cookie?.verminData?.api_token}`
       }}).then((res) => {
        if(res.data.features){
          setEditReportData(res.data.features);
          createReportModalOpen();
        }
      }).catch(async (err) => {
        console.log('axios Error')
      })
  }, [damage]);

/**
  * 情報登録モーダルフラグ
  * 20240412 Add Uduki 報告書登録ように追記
  */
  const [createDamageModalOpen,createReportModalOpen] = useCreateModal(state => [state.createDamageModalOpen,state.createReportModalOpen]);

/**
  * 編集モーダル開閉時のデータ格納ストア
  * 20240412 Add Uduki 報告書登録ように追記
  */
  const [setEditDamageData,setEditReportData] = useEditData(state => [state.setEditDamageData,state.setEditReportData]);

/**
  * 害獣情報修正モーダル を表示するハンドラ
  * @example
  * <Button onClick={handleEditModal} ... />
  */
  const handleEditModal = useCallback(() => {
    setEditDamageData(damage);
    createDamageModalOpen();
  }, [damage]);

//クリップボードにコピー関数
const copyToClipboard = useCallback(async () => {
  const url = window.location.origin +"/?corporate="+damage.client_code+"&S_lat="+damage.damage_latitude+"&S_lon="+damage.damage_longitude
  await global.navigator.clipboard.writeText(url);
  alert("URLをコピー")
},[damage]);

  return (
    <>
      <Marker
        position={latlng}
        icon={{
          url: markerFormat(damage.damage_type_id),
          anchor: new google.maps.Point(15, 10),
        }}
        onClick={handleMarkerClick}
        onRightClick={copyToClipboard}/*230915追記*/
        onDblClick={copyToClipboard}
        zIndex={damage.damage_id === activeDamageId ? 100 : 0}
      />
      {damage.damage_id === activeDamageId && (
        <InfoBox
          position={latlng}
          options={{
            ...infoBoxOptions,
            pixelOffset: new window.google.maps.Size(-135, -10),
          }}
        >
          <div className={styles.overview}>
            <button
              className={styles.closeButton}
              aria-label="詳細ボックスを閉じる"
              onClick={d_closeInfoBox}
            >
              <IconClose />
            </button>
            <ul>
              <li>{damage.damage_type_name && `被害種別: ${damage_category[damage.damage_type_id]}`}</li>
              <li>{damage.vermin_kind_name && `加害動物: ${damage.vermin_kind_name}`}</li>
              <li>{damage.discovery_date && `発生日: ${dayjs(damage.discovery_date).format('YYYY年MM月DD日')}`}</li>
              <li>{damage.discovery_date && `発生時間: ${damage.discovery_time.slice(0, 5)}`}</li>
              <li className={styles.remarks_hidden} style={{maxHeight: l_limit}}>{damage.damage_contents && `被害内容: ${damage.damage_contents}`}</li>
              <li>{damage.damage_implementation_name && `対策実施: ${damage.damage_implementation_name}`}</li>
              <li>{damage.damage_countermeasures && `対策内容: ${damage.damage_countermeasures}`}</li>
              <li>{damage.damage_implementation_date && `対策実施予定日: ${dayjs(damage.damage_implementation_date).format('YYYY年MM月DD日')}`}</li>
              <li className={styles.remarks_hidden} style={{maxHeight: l_limit}}>{damage.damage_remarks && `備考: ${damage.damage_remarks}`}</li>
            </ul>
            <div className={`${cookie?.verminData?.user_kind_id > 2 ? styles.row : styles.row2}`}>
              <Button onClick={handleEditModal} disabled={!cookie?.verminData?.user_kind_id || damage.client_code!==cookie?.verminData?.client_code || cookie?.verminData?.user_kind_id === 1 || (cookie?.verminData?.user_kind_id === 2 && cookie?.verminData?.user_info_id !== damage.update_user_id)}>
                情報編集
              </Button>
              <Button onClick={handleEditReportModal} disabled={!cookie?.verminData?.user_kind_id || damage.client_code!==cookie?.verminData?.client_code || cookie?.verminData?.user_kind_id === 1 || (cookie?.verminData?.user_kind_id === 2 && cookie?.verminData?.user_info_id !== damage.update_user_id)}>
                報告書編集
              </Button>
            </div>
          </div>
        </InfoBox>
      )}
    </>
  );
};

export default React.memo(DamageMarker);
